import { Box } from '@mantine/core'
import type { ReactNode } from 'react'

import './GradientHero.css'

const HERO_BACKGROUND =
  'radial-gradient(620px 260px at 88% -30%, color-mix(in srgb, var(--mantine-color-success-5) 26%, transparent), transparent 60%),' +
  'linear-gradient(135deg, #1e2952 0%, #131b32 100%)'

type GradientHeroProps = {
  children: ReactNode
  /** Optional centered inner column width (used by reading/landing layouts). */
  innerMaxWidth?: number
  /**
   * Span edge-to-edge past the page padding (like the onboarding home hero)
   * instead of rendering as a contained rounded card. The page must place this
   * outside its own max-width container for the bleed to reach the viewport.
   */
  fullBleed?: boolean
}

/**
 * Shared navy gradient hero shell (gradient + dotted texture), the intranet's
 * signature page header. Each feature composes its own content (badges, title,
 * search, stats, progress…) as children.
 */
export const GradientHero = ({
  children,
  innerMaxWidth,
  fullBleed
}: GradientHeroProps) => (
  <Box
    component="header"
    c="white"
    pos="relative"
    className={
      fullBleed ? 'overflow-hidden gradient-hero--bleed' : 'overflow-hidden'
    }
    bdrs={fullBleed ? '0 0 24px 24px' : 24}
    mb="xl"
    px={{ base: 'lg', sm: 'xl' }}
    pt={100}
    pb={40}
    style={{ background: HERO_BACKGROUND, zIndex: 10 }}
  >
    <Box
      aria-hidden
      pos="absolute"
      inset={0}
      style={{
        backgroundImage:
          'radial-gradient(color-mix(in srgb, white 7%, transparent) 1px, transparent 1.4px)',
        backgroundSize: '24px 24px',
        maskImage: 'linear-gradient(180deg, #000, transparent 82%)',
        WebkitMaskImage: 'linear-gradient(180deg, #000, transparent 82%)',
        pointerEvents: 'none'
      }}
    />

    <Box
      pos="relative"
      maw={innerMaxWidth}
      mx={innerMaxWidth ? 'auto' : undefined}
    >
      {children}
    </Box>
  </Box>
)
